<template>
  <div
    :class="[
      'single-blog-post  mx-auto max-w-screen-xl',
      'slug-' + singleBlogPost?.blogPost?.slug,
      loading ? 'cursor-wait' : '',
    ]"
  >
    <OrganismsBWTextOverlay
      v-if="brandWindow"
      class="h-[500px]"
      :organism="brandWindow"
    />
    <div
      class="flex flex-col md:grid md:grid-cols-[242px_1fr] max-w-5xl mx-auto md:gap-16 pt-14 px-8"
    >
      <aside class="order-last sidebar md:order-none">
        <OrganismsSidebarBlog />
      </aside>
      <main class="max-w-full pb-20 overflow-hidden">
        <h2
          v-if="singleBlogPost?.blogPost?.customPostDetails.subtitle"
          class="mb-6 text-2xl font-bold text-gray-800 subtitle font-lato md:text-5xl"
        >
          {{ singleBlogPost?.blogPost?.customPostDetails.subtitle }}
        </h2>
        <span class="mb-8 text-base text-primary-500 font-bold date font-lato line-clamp-1">{{
          convertDateFormat(singleBlogPost?.blogPost?.date) ?? ""
        }}</span>
        <div
          v-if="loading"
          class="p-8 font-medium text-center text-gray-500 font-figtree"
        >
          Loading...
        </div>
        <div
          v-if="!loading"
          v-html="singleBlogPost?.blogPost?.content"
          class="text-lg text-gray-800 main-content mb-14"
        ></div>
        <MoleculesSocialShareLinks :molecule="socialShare" />
      </main>
    </div>
    
    <MoleculesLoadingOverlay v-if="loading" />
  </div>
</template>

<style>
.single-blog-post {
  .main-content {
    h1 {
      @apply text-3xl mb-4 font-lato md:text-5xl;
    }
    h2 {
      @apply text-2xl mb-4 font-lato md:text-4xl;
    }
    h3 {
      @apply text-xl mb-4 font-lato md:text-2xl text-primary-500;
    }

    h4 {
      @apply text-lg mb-4 font-lato font-bold md:text-xl;
    }

    h5,
    h6 {
      @apply font-lato text-lg font-normal mb-8 md:text-xl text-primary-500;
    }

    p {
      @apply mb-8 overflow-hidden;
    }

    ul {
      @apply list-disc list-outside mb-8;

      li {
        @apply ml-5 mb-1;
      }
    }

    a {
      @apply text-primary-500 underline;
    }

    figure,
    img {
      @apply max-w-full;
    }

    .wp-block-image {
      @apply mb-8;
    }

    figcaption {
      @apply text-gray-500 text-base text-center pt-1 italic;
    }

    img {
      &.alignleft {
        @apply float-left mr-8 mb-8;
      }
      &.aligncenter {
        @apply mx-auto block mb-8;
      }
      &.alignright {
        @apply float-right ml-8 mb-8;
      }
    }

    hr {
      @apply w-1/2 mb-8 bg-accent-200 mx-auto;
    }

    strong {
      @apply font-bold;
    }

    .has-luminous-vivid-amber-color {
      @apply text-primary-500;
    }

    .is-layout-flex {
      @apply md:flex block justify-between items-center gap-3;

      .wp-block-image {
        @apply md:w-1/2 w-full min-w-full md:min-w-[50%];

        img {
          @apply !w-full;
        }
      }
    }
  }

  #three-column-contact.flexible-content-columns {
    @apply mb-20 pt-20 bg-gradient-to-b from-zinc-100 to-white;

    & > div {
      @apply border-2 border-gray-300 flex flex-col items-center justify-center p-10 gap-8 bg-white;
    }

    img {
      @apply w-20;
    }

    h3 {
      @apply font-lato font-semibold text-[28px] text-gray-700 leading-none;
    }

    .description {
      @apply text-lg text-gray-700 text-center;

      a {
        @apply underline text-secondary-600;
      }
    }
  }

  .wp-block-gallery {
    @apply grid grid-cols-1 lg:grid-cols-2 gap-4;

    img {
      @apply h-full object-cover;
    }
  }
}
</style>

<script setup>
const { path } = useRoute();
import { storeToRefs } from "pinia";
const blogPosts = useBlogPosts();
const { singleBlogPost, singleBlogPostLoading, singleBlogPostUri } =
  storeToRefs(blogPosts);

let loading = true;

watch(singleBlogPostLoading, (newLoading, oldLoading) => {
  loading = newLoading;
});

singleBlogPostUri.value = path;

const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
breadcrumbsList.value = [{ title: "Blog", link: "/blog/" }];

const brandWindow = computed(() => {
  const post = singleBlogPost?.value?.blogPost;
  if (!post) {
    return;
  }
  return {
    backgroundColor: "#ffffff",
    backgroundImageOpacity: 1,
    brandWindowLayout: "text-overlay-center",
    button1Color: "button-blue",
    button1Target: "_self",
    button2Color: "button-white",
    button2Target: "_self",
    button3Target: "_self",
    containerWidth: "full-width",
    contentTheme: "dark-text",
    title1: post.title,
    title2: "",
    type: "brand-window",
    backgroundImage: post.customPostDetails.image ?? {
      sourceUrl:
        "https://consortadmin.kinsta.cloud/wp-content/uploads/2025/03/DSC05173-2-scaled.jpg",
    },
  };
});

const socialShare = computed(() => {
  const post = singleBlogPost?.value?.blogPost;
  const shareData = post
    ? {
        shareUrl: "https://consort-homes.com/" + path,
        title: post.title,
        imgSrc: post.customPostDetails.image?.sourceUrl,
      }
    : {
        shareUrl: "https://consort-homes.com/",
        title: "Visit Consort Homes Neighorhoods",
        imgSrc:
          "https://consort-homes.com/img/logo-consort-white.svg",
      };

  return shareData;
});
</script>
